/**
 * Copyright © Amazon.com and Affiliates: This deliverable is considered Developed Content as defined in the AWS Service Terms
 * and the SOW between the parties dated 10/31/2024.
 */
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  inject,
  Input,
  OnInit,
  OnDestroy,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormBuilder, ReactiveFormsModule } from "@angular/forms";
import { Content, ContentType } from "../services/admin/content.service";
import { CommonModule } from "@angular/common";
import { SubscriptionsService } from "../services/user/subscriptions.service";
import { ActivatedRoute, Router } from "@angular/router";
import { TokenPropertyService } from "../services/token-property.service";
import { convertScheduleTimeCentralToEastern } from "../utils/timezone-utils";
import {
  emptySchedule,
  numOfDaysInMonth,
  daysOfWeekNumbers,
  SUBSCRIPTION_BREADCRUMBS,
  FREQUENCY_OPTIONS,
  DAILY_OPTIONS,
  REPEAT_AFTER_OPTIONS,
  DAYS_OF_WEEK,
  MONTHS_OF_YEAR,
  MONTHLY_OPTIONS,
  WEEKLY_REPEAT_OPTIONS,
  WEEK_OF_MONTH,
  TIME_OPTIONS,
  MINUTE_OPTIONS,
  AMPM_OPTIONS,
  MONTH_WEEKS,
  WEEKLY_REPEAT_CODE,
  MONTHS_WITH_DAYS,
} from "../../constants";
import { USER_ID_DEV } from "../../mock/user";
import { ParameterTableComponent } from "../parameter-table/parameter-table.component";
import { LoadingComponent } from "../loading/loading.component";
import { LoadingService } from "../services/loading.service";
import { SettingsService } from "../services/user/settings.service";
import { CloudWatchRumService } from '../services/cloudwatch-rum.service';
import { AuthContextService } from "../services/auth-context.service";

// Format options constants
const FORMAT_OPTIONS = {
  PDF: 'PDF',
  CSV: 'CSV', 
  EXCEL: 'EXCEL'
} as const;

const DASHBOARD_FORMAT_OPTIONS = [FORMAT_OPTIONS.PDF];
const REPORT_FORMAT_OPTIONS = [FORMAT_OPTIONS.CSV, FORMAT_OPTIONS.EXCEL];
const NULL_IDENTIFIER = 'NULL';

export type dropDownOptions = {
  name: string;
  value: string;
  selected: boolean;
};

// Interface for standardized API response format
interface UserSettingsResponse {
  settings: Array<{
    notificationInd: 'Y' | 'N';
    emailId: string;
    userId?: string;
    clientCode?: string;
    lastUpdDttm?: string;
  }>;
  featureFlags: Record<string, any>;
}

@Component({
  selector: "app-schedule-subscription",
  templateUrl: "./schedule-subscription.component.html",
  styleUrls: ["./schedule-subscription.component.css"],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ParameterTableComponent,
    LoadingComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ScheduleSubscriptionComponent implements OnInit, OnDestroy {
  @Input() subscribeItem!: Content | null;
  subscriptionsServices: SubscriptionsService = inject(SubscriptionsService);
  private cloudWatchRumService: CloudWatchRumService = inject(CloudWatchRumService);
  private authContextService: AuthContextService = inject(AuthContextService);
  @Output() clearSubscribeItem = new EventEmitter<void>();

  scheduleForm: any;
  isPageModeEdit: boolean = false;
  selectedSubscription: any;
  selectedDays: any;
  pageMode: string = "";
  dashboardId: string = "";
  parameters: any;
  formatOptions: string[] = [];
  requiredDateParameters: string[] = ['pDate', 'pAsOfDate', 'pBeginDate', 'pStartDate', 'pEndDate', 'pDateRange', 'pPersonDate'];
  invalidDateParameters: string[] = [];
  validationErrors: { [key: string]: string } = {};
  showValidationAlert: boolean = false;
  isLoading = false;
  apiErrorMessage: string = '';
  apiSuccessMessage: string = '';
  isApiSuccessModalVisible = false;
  isApiErrorModalVisible = false;

  // Method to check if form is ready for display
  isFormReady(): boolean {
    const hasForm = !!this.scheduleForm;
    const hasControls =
      hasForm && Object.keys(this.scheduleForm.controls).length > 0;
    const controlCount = hasControls
      ? Object.keys(this.scheduleForm.controls).length
      : 0;
    const hasRequiredControls =
      hasForm &&
      ["subscriptionName", "frequency", "format", "startDate", "endDate"].every(
        (control) => this.scheduleForm.contains(control)
      );

    const isReady =
      hasForm &&
      hasControls &&
      controlCount >= 40 &&
      hasRequiredControls &&
      this.isFormInitialized &&
      !this.isFormLocked;

    return isReady;
  }

  // Method to get initial parameter values for edit mode
  getInitialParameterValues(): any {
    if (
      !this.objectOfExistingValues ||
      !this.objectOfExistingValues.parameters
    ) {
      return {};
    }

    return this.objectOfExistingValues.parameters;
  }

  getParameterValidationErrors(): string[] {
    const errors: string[] = [];
    const requiredDateParamsLower = this.requiredDateParameters.map(p => p.toLowerCase());
    
    // Check all validation errors for parameter-related errors
    Object.keys(this.validationErrors).forEach(key => {
      const normalizedKey = key.toLowerCase();
      if (requiredDateParamsLower.includes(normalizedKey)) {
        errors.push(this.validationErrors[key]);
      }
    });
    
    return errors;
  }

  getErrorMessage(parameterName: string): string {
    return this.validationErrors[parameterName] || '';
  }

  hasValidationErrors(): boolean {
    return Object.keys(this.validationErrors).length > 0 || this.invalidDateParameters.length > 0;
  }

  items = SUBSCRIPTION_BREADCRUMBS;

  frequencyOptions = FREQUENCY_OPTIONS;

  dailyOptions = DAILY_OPTIONS;

  repeatAfterOptions = REPEAT_AFTER_OPTIONS;

  daysOfWeek = DAYS_OF_WEEK.map((day, index) => {
    return {
      checked: false,
      disabled: false,
      labelText: day,
      initialValue: day.toLowerCase(),
      dayNumber: index + 1,
    };
  });

  monthsOfYearCheckbox = MONTHS_OF_YEAR.map((month, index) => {
    return {
      checked: false,
      disabled: false,
      labelText: month,
      initialValue: index + 1,
    };
  });

  monthlyOptions = MONTHLY_OPTIONS;

  monthsByQuarter = [
    this.monthsOfYearCheckbox.slice(0, 3),
    this.monthsOfYearCheckbox.slice(3, 6),
    this.monthsOfYearCheckbox.slice(6, 9),
    this.monthsOfYearCheckbox.slice(9, 12),
  ];

  monthsOfYearSelect = this.monthsOfYearCheckbox.map((month, index) => {
    return {
      value: index + 1,
      name: month.labelText,
      selected: false,
    };
  });

  weeklyRepeatOptions = WEEKLY_REPEAT_OPTIONS;

  weekOfMonth = WEEK_OF_MONTH;

  // Time options with 12-hour format (1-12)
  timeOptions = TIME_OPTIONS;

  // New options for minutes
  minuteOptions = MINUTE_OPTIONS;

  // AM/PM options
  ampmOptions = AMPM_OPTIONS;

  monthWeeks = MONTH_WEEKS;

  weeklyRepeatCode = WEEKLY_REPEAT_CODE;

  monthsWithDays = MONTHS_WITH_DAYS;

  numberOfDays: number[] = [];

  monthlySelection = "";
  daysOfMonthSelected = "";

  daysInMonthSelectOptions = [] as dropDownOptions[];

  timezoneOptions = [] as dropDownOptions[];
  tokenDetails: any;
  userId: string = USER_ID_DEV;
  objectOfExistingValues: any;
  isFormInitialized: boolean = false;
  isFormLocked: boolean = false;
  
  // Notification settings properties
  globalNotificationsEnabled: boolean = false;
  showNotificationAlert: boolean = false;
  isLoadingSettings: boolean = false;
  private notificationStateSet: boolean = false;
  private visibilityChangeHandler?: () => void;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private actRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private loadingService: LoadingService,
    private settingsService: SettingsService,
    private tokenPropertyService: TokenPropertyService
  ) {}

  ngOnInit() {
    // Initialize timezone options with basic US timezones for now
    this.timezoneOptions = [
      { name: 'US/Eastern', value: 'US/Eastern', selected: false },
      { name: 'US/Central', value: 'US/Central', selected: false },
      { name: 'US/Mountain', value: 'US/Mountain', selected: false },
      { name: 'US/Pacific', value: 'US/Pacific', selected: false }
    ];

    this.selectedSubscription = {};
    this.isPageModeEdit = false;
    this.initForm();

    // Ensure the form has today's date as the default start date
    const today = new Date();
    const todayStr = this.formatDateForDisplay(today);
    this.scheduleForm.get("startDate")?.setValue(todayStr);

    this.userId = this.tokenPropertyService.getPropertyFromToken('sub', 'token');

    this.actRoute.queryParams.subscribe((params: any) => {
      this.pageMode = params["pageMode"];
    });

    if (this.pageMode === "edit") {
      this.subscriptionsServices.selectedSubscription$.subscribe((sub: any) => {
        if (sub && sub.description) {
          this.isPageModeEdit = true;
          const subDescription = sub.description;

          this.objectOfExistingValues = JSON.parse(subDescription);

          // Populate parameters from the description's top-level parameter fields first.
          // The description is valid JSON, so values with apostrophes are preserved correctly.
          // This is the primary source and avoids failures when parsing the Python-dict
          // parameters string (which breaks on client names like "McDonald's").
          if (!this.objectOfExistingValues.parameters) {
            this.objectOfExistingValues.parameters = {};
          }
          Object.keys(this.objectOfExistingValues).forEach((key) => {
            if (/^p[A-Z]/.test(key)) {
              this.objectOfExistingValues.parameters[key] =
                this.objectOfExistingValues[key];
            }
          });

          // Also parse the parameters field if it exists, to override with any
          // more up-to-date values (only applied when parsing succeeds).
          if (sub.parameters) {
            try {
              let parsedParameters: any;

              if (typeof sub.parameters === "string") {
                // The parameters field may be in Python dict format; convert to JSON.
                // NOTE: simple quote replacement fails on values containing apostrophes
                // (e.g. "McDonald's"), so we only apply the override when parsing succeeds.
                let parametersString: string = sub.parameters;

                // Convert Python dict format to JSON format
                parametersString = parametersString
                  .replace(/'/g, '"') // Replace single quotes with double quotes
                  .replace(/True/g, "true") // Replace Python True with JSON true
                  .replace(/False/g, "false") // Replace Python False with JSON false
                  .replace(/None/g, "null"); // Replace Python None with JSON null

                parsedParameters = JSON.parse(parametersString);
              } else if (
                typeof sub.parameters === "object" &&
                sub.parameters !== null
              ) {
                // Parameters field is already an object
                parsedParameters = sub.parameters;
              }

              // Override with the parsed parameters (they should be more up-to-date)
              if (parsedParameters) {
                Object.assign(
                  this.objectOfExistingValues.parameters,
                  parsedParameters
                );
              }
            } catch (paramError) {
              // Parsing failed (e.g. apostrophes in values) — keep the parameters
              // populated from the description JSON above.
            }
          }

          this.selectedSubscription = sub;
          // Re-initialize form with correct format options now that we have subscription data
          this.initForm();
        }
      });
    }

    this.setBreadcrumbs();

    this.scheduleForm
      .get("monthSelected")
      ?.valueChanges.subscribe((monthSelected: any) => {
        this.updateNumberOfDays(monthSelected);
      });

    this.dashboardId = this.isPageModeEdit
      ? this.selectedSubscription.dashboardId.trim()
      : this.subscribeItem?.dashboardId.trim();

    this.setDefaultSubscriptionDates();
    
    // Check user notification settings - this will update the form when settings are loaded
    this.checkUserNotificationSettings();
    
    // Listen for page visibility changes to refresh settings
    this.setupPageVisibilityListener();
  }

  ngAfterViewInit() {
    if (this.objectOfExistingValues) {
        // Map old pDateRange labels to new ones for backward compatibility
        if (this.objectOfExistingValues.parameters?.pDateRange) {
          const dateRangeLabelMapping: Record<string, string> = {
            'Last Week': 'Last 7 days',
            'Last Month': 'Last 30 days',
            'Last Quarter': 'Last 90 days',
            'Last Year': 'Last 365 days'
          };
        
          const oldLabel = this.objectOfExistingValues.parameters.pDateRange;
          if (dateRangeLabelMapping[oldLabel]) {
            this.objectOfExistingValues.parameters.pDateRange = dateRangeLabelMapping[oldLabel];
          }
        }
      
      // Handle potential time conversion for existing values
      let easternHour12 = parseInt(this.objectOfExistingValues.time) || 12;
      let easternAmpm = this.objectOfExistingValues.ampm || 'AM';
      let easternMinute = parseInt(this.objectOfExistingValues.minute) || 0;
      
      // Convert Eastern 12-hour format to 24-hour format first
      let easternHour24: number;
      if (easternAmpm === 'AM') {
        easternHour24 = easternHour12 === 12 ? 0 : easternHour12;
      } else { // PM
        easternHour24 = easternHour12 === 12 ? 12 : easternHour12 + 12;
      }
      
      // Convert Eastern time (24-hour) to Central time for display
      const centralTime = this.convertEasternToCentralDisplay(easternHour24, easternMinute);

      // Create a form group with existing values but use converted time values
      // Start with the default structure and override with existing values

      const formConfig: any = {
        ...emptySchedule,
        minute: [0],
        ampm: ["AM"],
      };

      // Safely apply existing values, ensuring they're in the correct format for FormBuilder
      if (this.objectOfExistingValues) {
        Object.keys(this.objectOfExistingValues).forEach((key) => {
          if (key in formConfig && key !== 'time' && key !== 'minute' && key !== 'ampm') {
            
            // Skip format field in edit mode - we'll handle it separately after setting format options
            if (key === 'format' && this.isPageModeEdit) {
              return;
            }            
            // Ensure value is wrapped in array for FormBuilder
            const value = this.objectOfExistingValues[key];
            const formattedValue = Array.isArray(value) ? value : [value];

            formConfig[key] = formattedValue;
          }
        });
      }
      // Override time-related values with converted Central time values for display
      formConfig.time = [centralTime.hour];
      formConfig.minute = [centralTime.minute];
      formConfig.ampm = [centralTime.ampm];

      this.scheduleForm = this.fb.group(formConfig);

      // Set form as initialized after creation
      this.isFormInitialized = true;

      // If in edit mode and the subscription has a name, use it
      // Otherwise, if the form doesn't have a subscription name, set a default one
      if (this.selectedSubscription?.subscriptionName) {
        this.scheduleForm
          .get("subscriptionName")
          ?.setValue(this.selectedSubscription.subscriptionName);
      } else if (
        !this.scheduleForm.get("subscriptionName")?.value &&
        this.selectedSubscription?.title
      ) {
        const defaultSubscriptionName = `${this.selectedSubscription.title} Subscription`;
        this.scheduleForm
          .get("subscriptionName")
          ?.setValue(defaultSubscriptionName);
      }
    } else {
      // Ensure start date is set to today
      const today = new Date();
      const todayStr = this.formatDateForDisplay(today);
      this.scheduleForm.get("startDate")?.setValue(todayStr);
    }

    // Safeguard: Handle notification based on global settings
    const notificationValue = this.scheduleForm.get("notification")?.value;
    if (!this.globalNotificationsEnabled) {
      // If global notifications are off, force local notification to false and disable
      this.scheduleForm.get("notification")?.setValue(false);
      this.scheduleForm.get("notification")?.disable();
    } else if (notificationValue === null || notificationValue === undefined) {
      // If global notifications are on and no value set, default to true
      this.scheduleForm.get("notification")?.setValue(true);
    }

    // Ensure notification state is updated after form initialization
    this.updateNotificationFormState();

    // For yearly subscriptions in edit mode, populate the days dropdown
    if (this.isPageModeEdit && this.scheduleForm.get("frequency")?.value === "yearly") {
      const monthValue = this.scheduleForm.get("monthSelected")?.value;
      if (monthValue) {
        this.updateNumberOfDays(monthValue);
      }
    }

    this.cdr.detectChanges();
  }

  private setBreadcrumbs() {
    if (this.isPageModeEdit) {
      this.items = [
        { name: "Home", url: "/" },
        { name: "Subscriptions", url: "/subscriptions" },
        { name: "Edit Subscription", url: "/subscriptions" },
      ];
    } else {
      this.items = [
        { name: "Home", url: "/" },
        { name: "Reports & Dashboards", url: "/reports-and-dashboards" },
        { name: "Subscribe", url: "/reports-and-dashboards" },
      ];
    }
  }

  onBreadcrumbSelected(event: Event) {
    const payload = (
      event as CustomEvent<{
        name: string;
        url: string;
      }>
    ).detail;

    if (payload.url === "/reports-and-dashboards") {
      this.clearSubscribeItem.emit();
    } else {
      this.router.navigate([payload.url]);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["subscribeItem"] && changes["subscribeItem"].currentValue) {
      this.initForm();
    }
  }

  // Platform entitlement is authorized server-side (backend returns 403 when denied);
  // the UI must not pre-filter subscribe eligibility using a client-side/JWT platform claim.

  initForm() {
    this.scheduleForm = this.fb.group({
      ...emptySchedule,
      // Add new time related controls
      minute: [0],
      ampm: ["AM"],
    });

    // Set form as initialized after creation
    this.isFormInitialized = true;
    // Reset notification state flag so it can be set again
    this.notificationStateSet = false;

    // Clear validation alert when form is modified
    this.scheduleForm.valueChanges.subscribe(() => {
      if (this.showValidationAlert && this.scheduleForm.dirty) {
        this.showValidationAlert = false;
        this.invalidDateParameters = [];
      }
    });

    // Set format options based on mode
    if (this.isPageModeEdit && this.selectedSubscription) {
      /**
       * Determine format options for edit mode based on subscription type:
       * - Dashboard subscriptions: visualId/sheetId are NULL AND format is PDF → PDF only
       * - Report subscriptions: any other combination → CSV/EXCEL options
       * This logic handles edge cases where reports may also have NULL visualId/sheetId
       * but can be distinguished by their CSV/EXCEL format.
       */
      const hasNullVisualAndSheet = (
        (!this.selectedSubscription.visualId || 
         this.selectedSubscription.visualId === NULL_IDENTIFIER || 
         !this.selectedSubscription.visualId.trim()) &&
        (!this.selectedSubscription.sheetId || 
         this.selectedSubscription.sheetId === NULL_IDENTIFIER || 
         !this.selectedSubscription.sheetId.trim())
      );
      
      const isDashboardSubscription = hasNullVisualAndSheet && 
                                     this.selectedSubscription.format === FORMAT_OPTIONS.PDF;
      
      if (isDashboardSubscription) {
        // Dashboard subscriptions: only show PDF (cannot be changed)
        this.formatOptions = DASHBOARD_FORMAT_OPTIONS;
        this.scheduleForm.get("format")?.setValue(FORMAT_OPTIONS.PDF);
      } else {
        // Report subscriptions: allow CSV/EXCEL selection
        this.formatOptions = REPORT_FORMAT_OPTIONS;
        // Mark for check and detect changes to update template
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        // Use Promise.resolve to defer value setting until after view update
        Promise.resolve().then(() => {
          this.scheduleForm.get("format")?.setValue(this.selectedSubscription.format);
        });
      }
    } else if (this.subscribeItem) {
      // In create mode: determine options by content type
      this.formatOptions =
        this.subscribeItem?.contentType.toUpperCase() === ContentType.DASHBOARD
          ? DASHBOARD_FORMAT_OPTIONS
          : REPORT_FORMAT_OPTIONS;
      this.scheduleForm
        .get("format")
        ?.setValue(this.formatOptions.length === 1 ? this.formatOptions[0] : "");
    } else {
      // Fallback: default to report options
      this.formatOptions = REPORT_FORMAT_OPTIONS;
    }

    this.setDefaultSubscriptionDates();

    // Update notification state after form creation - this will respect global settings
    this.updateNotificationFormState();
  }

  onFrequencyChanged(event: any) {
    // Reset all form controls related to recurrence options
    this.scheduleForm.controls["dailyOption"].reset();
    this.scheduleForm.controls["weeklyRepeat"].reset();
    this.scheduleForm.controls["repeatDays"].reset();
    this.scheduleForm.controls["monthlyOption"].reset();
    this.scheduleForm.controls["weekOfMonth"].reset();
    this.scheduleForm.controls["calendarDays"].reset();
    this.scheduleForm.controls["monthSelected"].reset();
    this.scheduleForm.controls["daysOfTheMonthSelected"].reset();

    // Reset all day checkboxes
    this.daysOfWeek.forEach((day) =>
      this.scheduleForm.controls[day.initialValue].reset()
    );

    // Reset all month checkboxes
    this.monthsOfYearSelect.forEach((month) =>
      this.scheduleForm.controls[month.name].reset()
    );

    // Set default options based on the selected frequency
    const frequency = this.scheduleForm.get("frequency")?.value;

    if (frequency === "daily") {
      // Set default daily option
      this.scheduleForm.controls["dailyOption"].setValue("onDays");
    } else if (frequency === "weekly") {
      // Set default weekly option
      this.scheduleForm.controls["weeklyRepeat"].setValue("1");
    } else if (frequency === "monthly") {
      // Set default monthly option
      this.scheduleForm.controls["monthlyOption"].setValue("onWeekOfMonth");
      this.scheduleForm.controls["weekOfMonth"].setValue("1");
    } else if (frequency === "yearly") {
      // Set default yearly options
      this.scheduleForm.controls["monthSelected"].setValue(1);
      this.updateNumberOfDays(1);
      this.scheduleForm.controls["daysOfTheMonthSelected"].setValue(1);
    }

    // Make sure time is always set (default to 9:00 AM Central Time if not already set)
    if (!this.scheduleForm.get("time")?.value) {
      this.scheduleForm.controls["time"].setValue(9);
    }

    // Set default values for minute and AM/PM if not already set
    if (
      !this.scheduleForm.get("minute")?.value &&
      this.scheduleForm.get("minute")?.value !== 0
    ) {
      this.scheduleForm.controls["minute"].setValue(0);
    }

    if (!this.scheduleForm.get("ampm")?.value) {
      this.scheduleForm.controls["ampm"].setValue("AM");
    }

    this.cdr.detectChanges();
  }

  onDailyOptionChanged(event: any) {
    if (this.scheduleForm.get("dailyOption")?.value !== "onDays") {
      this.daysOfWeek.forEach((day) =>
        this.scheduleForm.controls[day.initialValue].reset()
      );
    }

    // Clear daily schedule validation error when option changes
    if (this.validationErrors["dailySchedule"]) {
      this.validationErrors["dailySchedule"] = "";
    }

    this.scheduleForm.controls["weeklyRepeat"].reset();
    this.scheduleForm.controls["repeatDays"].reset();
    this.monthsOfYearSelect.forEach((month) =>
      this.scheduleForm.controls[month.name].reset()
    );
    this.scheduleForm.controls["monthSelected"].reset();
    this.scheduleForm.controls["daysOfTheMonthSelected"].reset();
  }

  onDaySelectionChanged() {
    if (!this.scheduleForm) {
      return;
    }
    // Clear validation error when user selects/deselects days
    const frequency = this.scheduleForm.get("frequency")?.value;
    const dailyOption = this.scheduleForm.get("dailyOption")?.value;
    
    if (frequency === "daily" && dailyOption === "onDays") {
      const selectedDays = this.daysOfWeek.some(day => 
        this.scheduleForm.get(day.initialValue)?.value
      );
      
      if (selectedDays && this.validationErrors["dailySchedule"]) {
        this.validationErrors["dailySchedule"] = "";
      }
    }
  }

  onSubmit() {
    if (!this.validateSubscriptionDates()) {
      return;
    }

    // Show loading spinner when submitting
    this.isLoading = true;
    this.apiErrorMessage = '';
    this.apiSuccessMessage = '';

    // Get converted Eastern time (24-hour format) using our helper method
    const hour = this.getHourIn24Format();
    
    // Get minute from converted Central to Eastern time
    const centralHour12 = parseInt(this.scheduleForm.get("time").value);
    const centralAmpm = this.scheduleForm.get("ampm").value;
    const centralMinute = parseInt(this.scheduleForm.get("minute").value) || 0;
    
    // Convert Central display time to Eastern storage time
    const easternTime = this.convertCentralToEasternStorage(centralHour12, centralMinute, centralAmpm);
    const minute = easternTime.minute;

    const days = this.daysOfWeek
      .filter((day) => this.scheduleForm.get(day.initialValue)?.value)
      .map(
        (day) =>
          daysOfWeekNumbers[day.initialValue as keyof typeof daysOfWeekNumbers]
      );

    let cronString = "";
    switch (this.scheduleForm.get("frequency")?.value) {
      case "daily":
        switch (this.scheduleForm.get("dailyOption")?.value) {
          case "onDays":
            cronString += `${minute} ${hour} ? * ${days.join(",")} *`;
            break;
          case "everyWeekday":
            cronString += `${minute} ${hour} ? * MON-FRI *`;
            break;
          case "repeatAfter":
            cronString += `${minute} ${hour} */${
              this.scheduleForm.get("repeatDays").value
            } * ? *`;
            break;
          default:
            break;
        }
        break;
      case "weekly":
        const weeklyDaysString = days.length > 0 ? days.join(",") : "*";
        const weeklyRepeat =
          this.weeklyRepeatCode[
            this.scheduleForm.get("weeklyRepeat")
              .value as keyof typeof this.weeklyRepeatCode
          ];

        if (days.length > 0) {
          cronString += `${minute} ${hour} ? * ${weeklyDaysString} *`;
        } else {
          cronString += `${minute} ${hour} ${weeklyRepeat}/7 * ? *`;
        }
        break;
      case "monthly":
        const monthsString =
          this.monthsOfYearSelect
            .filter((month) => this.scheduleForm.get(month.name)?.value)
            .map((month) => month.value)
            .join(",") || "*";
        let monthlyDaysString =
          this.scheduleForm.get("monthlyOption")?.value === "onWeekOfMonth"
            ? this.scheduleForm.get("weekOfMonth").value
            : this.scheduleForm.get("calendarDays").value;
        cronString +=
          this.scheduleForm.get("monthlyOption")?.value === "onWeekOfMonth"
            ? `${minute} ${hour} ${
                this.monthWeeks[
                  this.scheduleForm.get("weekOfMonth")
                    .value as keyof typeof this.monthWeeks
                ]
              }/7 ${monthsString} ? *`
            : `${minute} ${hour} ${monthlyDaysString} ${monthsString} ? *`;
        break;
      case "yearly":
        cronString += `${minute} ${hour} ${
          this.scheduleForm.get("daysOfTheMonthSelected").value
        } ${this.scheduleForm.get("monthSelected").value} ? *`;
        break;
      default:
        break;
    }

    const subscriptionStartDate = this.formatDateForAPI(
      this.scheduleForm.get("startDate")?.value
    );
    const subscriptionEndDate = this.formatDateForAPI(
      this.scheduleForm.get("endDate")?.value
    );

    const subscriptionName = this.scheduleForm.get("subscriptionName")?.value;

    // Create a modified form data object with Eastern time for storage
    const contentId = this.isPageModeEdit
      ? this.selectedSubscription.contentId || this.selectedSubscription.contentid || this.objectOfExistingValues?.contentId || this.objectOfExistingValues?.contentid
      : this.subscribeItem?.contentId;
    const formDataForStorage = { ...this.scheduleForm.value, ...(contentId ? { contentId } : {}) };
    
    // Convert Eastern 24-hour back to 12-hour format for storage in description
    // (reuse the easternTime already calculated above)
    let easternHour12: number;
    let easternAmpm: string;
    
    if (easternTime.hour24 === 0) {
      easternHour12 = 12;
      easternAmpm = 'AM';
    } else if (easternTime.hour24 < 12) {
      easternHour12 = easternTime.hour24;
      easternAmpm = 'AM';
    } else if (easternTime.hour24 === 12) {
      easternHour12 = 12;
      easternAmpm = 'PM';
    } else {
      easternHour12 = easternTime.hour24 - 12;
      easternAmpm = 'PM';
    }
    
    // Update the form data with Eastern time values
    formDataForStorage.time = easternHour12.toString();
    formDataForStorage.minute = easternTime.minute.toString();
    formDataForStorage.ampm = easternAmpm;

    this.subscriptionsServices
      .createUserSubscription({
        subscriptionId: this.isPageModeEdit
          ? this.selectedSubscription.subscriptionId
          : "",
        dashboardId: this.isPageModeEdit
          ? this.selectedSubscription.dashboardId.trim()
          : this.subscribeItem?.dashboardId.trim(),
        userId: this.userId,
        status: "ACTIVE",
        notification: this.scheduleForm.get("notification")?.value ? "Y" : "N",
        cronSchedule: `cron(${cronString})`,
        description: JSON.stringify(formDataForStorage),
        sheetId: this.isPageModeEdit
          ? this.selectedSubscription.sheetId
          : this.subscribeItem?.sheetId,
        format: this.scheduleForm.get("format")?.value,
        subscriptionName: subscriptionName,
        title: this.isPageModeEdit
          ? this.selectedSubscription.title
          : this.subscribeItem?.title,
        visualId: this.isPageModeEdit
          ? this.selectedSubscription.visualId
          : this.subscribeItem?.visualId,
        parameters: this.parameters,
        subscriptionStartDate: subscriptionStartDate,
        subscriptionEndDate: subscriptionEndDate,
      })
      .subscribe({
        next: (response: any) => {
          // Hide loading spinner on success
          this.isLoading = false;

          // Handle HTTP 200 responses that carry a server-side error in the body
          const hiddenError = this.resolveApiError(response);
          if (hiddenError) {
            this.apiErrorMessage = hiddenError;
            this.isApiErrorModalVisible = true;
            return;
          }

          // Track subscription creation event
          this.cloudWatchRumService.addCustomEvent('subscription_created', {
            contentId,
            frequency: this.scheduleForm.get('frequency')?.value ?? null,
            format: this.scheduleForm.get('format')?.value ?? null,
            deliveryChannel: this.scheduleForm.get('notification')?.value ? 'email' : 'none',
          });

          if (this.isPageModeEdit === true) {
            // Navigate to the correct subscriptions route carrying the success message in router state
            const subscriptionsRoute = this.authContextService.hasInternalAccess
              ? '/internal/subscriptions'
              : '/subscriptions';
            this.router.navigate([subscriptionsRoute], {
              state: { successMessage: 'Subscription updated successfully.' }
            });
          } else {
            // Show success modal briefly before clearing the form
            this.apiSuccessMessage = 'Subscription created successfully.';
            this.isApiSuccessModalVisible = true;
            setTimeout(() => {
              this.isApiSuccessModalVisible = false;
              this.clearSubscribeItem.emit();
            }, 2000);
          }
        },
        error: (error: any) => {
          // Hide loading spinner on error
          this.isLoading = false;

          console.error("Error saving subscription:", error.message);
          if (error?.status === 403) {
            this.apiErrorMessage = error?.error?.message
              || 'You do not have an active entitlement for this content. The subscription was not saved.';
          } else {
            this.apiErrorMessage = this.isPageModeEdit
              ? 'Failed to update subscription. Please try again.'
              : 'Failed to create subscription. Please try again.';
          }
          this.isApiErrorModalVisible = true;
        },
      });
  }

  /**
   * Detects server-side errors embedded inside an HTTP 200 response body.
   * The lambda may return statusCode 500 with an error/errorMessage field
   * even when the HTTP transport status is 200.
   */
  private resolveApiError(response: any): string | null {
    if (!response) return null;

    const hasEmbeddedError =
      (response.statusCode && response.statusCode >= 400) ||
      response.error ||
      response.errorMessage;

    if (!hasEmbeddedError) return null;

    const errorType: string = response.errorType || '';
    const errorMessage: string = response.errorMessage || response.error || '';

    console.error('Embedded API error in 200 response:', response);

    // ValidationException: schedule will never fire within the given date range
    if (
      errorType === 'ValidationException' ||
      errorMessage.toLowerCase().includes('will never schedule an invocation')
    ) {
      return 'The schedule will never run within the selected date range. Please adjust the start/end dates or the schedule frequency.';
    }

    // Return the raw server message for any other known error
    if (errorMessage) {
      return `Subscription could not be saved: ${errorMessage}`;
    }

    // Generic fallback
    return this.isPageModeEdit
      ? 'Failed to update subscription. Please try again.'
      : 'Failed to create subscription. Please try again.';
  }

  onClear() {
    // Reset the form but make sure to include our new controls
    this.scheduleForm.reset({
      ...emptySchedule,
      minute: 0,
      ampm: "AM",
    });
    this.scheduleForm.get("notification")?.patchValue(true);
    this.validationErrors = {};
    this.setDefaultSubscriptionDates();
  }

  updateNumberOfDays(value: number) {
    const month = this.monthsWithDays.find((m) => m.value == value);
    if (month) {
      this.numberOfDays = Array.from({ length: month.days }, (_, i) => i + 1);
    }
    this.scheduleForm.get("selectedDay")?.patchValue("");
  }

  onMonthlyOptionChanged(event: any) {
    this.scheduleForm.controls["weekOfMonth"].reset();
    this.scheduleForm.controls["calendarDays"].reset();
  }

  updateParameters(event: any) {
    try {
      this.parameters = event;

      // Defensive check: ensure parameters is a valid object
      if (!this.parameters || typeof this.parameters !== "object") {
        this.parameters = {};
        return;
      }

      // Pass-through: rely on API/ParameterTable to provide correct date params.
      // Do not infer or re-map date ranges locally; the API owns canonical strings.

      // UI rule: if both pBeginDate and pEndDate are present, remove them
      // and render a date range select via pDateRange.
      const keys = Object.keys(this.parameters);
      // Support either pBeginDate or pStartDate as the begin date, and pEndDate as end
      const beginKey = keys.find(k => {
        const lower = k.toLowerCase();
        return lower === 'pbegindate' || lower === 'pstartdate';
      });
      const endKey = keys.find(k => k.toLowerCase() === 'penddate');
      if (beginKey && endKey) {
        // Remove raw date parameters
        delete this.parameters[beginKey];
        delete this.parameters[endKey];
        // Ensure pDateRange exists for the UI select; preserve existing if provided
        if (!('pDateRange' in this.parameters)) {
          this.parameters['pDateRange'] = '';
        }
      }

      // If we have parameters but they're not in the form, add them
      if (this.parameters && Object.keys(this.parameters).length > 0) {
        Object.keys(this.parameters).forEach((paramName) => {
          try {
            const paramValue = this.parameters[paramName];

            if (!this.scheduleForm.contains(paramName)) {
              this.scheduleForm.addControl(
                paramName,
                this.fb.control(paramValue)
              );
            } else {
              this.scheduleForm
                .get(paramName)
                ?.setValue(this.parameters[paramName]);
            }

          } catch (paramError) {}
        });
      } else {
      }
    } catch (error) {
      this.parameters = {}; // Fallback to empty parameters
    }
  }

  getRecurrenceSummary(): string {
    if (!this.scheduleForm) {
      return "";
    }

    const frequency = this.scheduleForm.get("frequency")?.value;
    const timeValue = this.scheduleForm.get("time")?.value;
    const minuteValue = this.scheduleForm.get("minute")?.value || 0;
    const ampmValue = this.scheduleForm.get("ampm")?.value || "AM";

    if (!frequency) {
      return "";
    }

    // Format the time with hours, minutes and AM/PM (displayed in Central Time)
    const hour = parseInt(timeValue);
    const minuteStr = minuteValue.toString().padStart(2, "0");
    const timeStr = `${hour}:${minuteStr} ${ampmValue} CT`;

    const startDate = this.scheduleForm.get("startDate")?.value
      ? new Date(this.scheduleForm.get("startDate")?.value).toLocaleDateString()
      : "immediately";

    const endDate = this.scheduleForm.get("endDate")?.value
      ? new Date(this.scheduleForm.get("endDate")?.value).toLocaleDateString()
      : "";

    let recurrence = "";

    switch (frequency) {
      case "daily":
        const dailyOption = this.scheduleForm.get("dailyOption")?.value;
        if (dailyOption === "onDays") {
          const selectedDays = this.daysOfWeek
            .filter((day) => this.scheduleForm.get(day.initialValue)?.value)
            .map((day) => day.labelText);

          if (selectedDays.length === 0) {
            recurrence = `every day`;
          } else if (selectedDays.length === 7) {
            recurrence = `every day`;
          } else {
            recurrence = `every ${selectedDays.join(", ")}`;
          }
        } else if (dailyOption === "everyWeekday") {
          recurrence = `every weekday (Monday-Friday)`;
        } else if (dailyOption === "repeatAfter") {
          const repeatDays = this.scheduleForm.get("repeatDays")?.value;
          if (repeatDays) {
            recurrence =
              repeatDays === "1" ? `every day` : `every ${repeatDays} days`;
          } else {
            recurrence = `every day`;
          }
        } else {
          recurrence = `every day`;
        }
        break;

      case "weekly":
        const weeklyRepeat = this.scheduleForm.get("weeklyRepeat")?.value;
        const weeklyRepeatOption = this.weeklyRepeatOptions.find(
          (option) => option.value === weeklyRepeat
        );
        const weeklySelectedDays = this.daysOfWeek
          .filter((day) => this.scheduleForm.get(day.initialValue)?.value)
          .map((day) => day.labelText);

        if (weeklySelectedDays.length === 0) {
          recurrence = `${weeklyRepeatOption?.name || "every week"}`;
        } else if (weeklySelectedDays.length === 7) {
          recurrence = `${
            weeklyRepeatOption?.name || "every week"
          } on every day`;
        } else {
          recurrence = `${
            weeklyRepeatOption?.name || "every week"
          } on ${weeklySelectedDays.join(", ")}`;
        }
        break;

      case "monthly":
        const selectedMonths = this.monthsOfYearSelect
          .filter((month) => this.scheduleForm.get(month.name)?.value)
          .map((month) => month.name);

        const monthsText =
          selectedMonths.length > 0
            ? `in ${selectedMonths.join(", ")}`
            : "every month";

        const monthlyOption = this.scheduleForm.get("monthlyOption")?.value;

        if (monthlyOption === "onWeekOfMonth") {
          const weekOfMonth = this.scheduleForm.get("weekOfMonth")?.value;
          if (weekOfMonth) {
            recurrence = `on days ${
              this.monthWeeks[weekOfMonth as keyof typeof this.monthWeeks]
            } ${monthsText}`;
          } else {
            recurrence = `${monthsText}`;
          }
        } else if (monthlyOption === "onCalendarDays") {
          const calendarDays = this.scheduleForm.get("calendarDays")?.value;
          if (calendarDays) {
            recurrence = `on day${
              calendarDays.includes(",") || calendarDays.includes("-")
                ? "s"
                : ""
            } ${calendarDays} ${monthsText}`;
          } else {
            recurrence = `${monthsText}`;
          }
        } else {
          recurrence = `${monthsText}`;
        }
        break;

      case "yearly":
        const monthValue = this.scheduleForm.get("monthSelected")?.value;
        const monthName =
          this.monthsWithDays.find((m) => m.value == monthValue)?.name || "";
        const dayValue = this.scheduleForm.get("daysOfTheMonthSelected")?.value;

        if (monthName && dayValue) {
          recurrence = `yearly on ${monthName} ${dayValue}`;
        } else if (monthName) {
          recurrence = `yearly in ${monthName}`;
        } else {
          recurrence = `yearly`;
        }
        break;

      default:
        return "";
    }

    return `Occurs ${recurrence}${
      timeStr ? " at " + timeStr : ""
    } starting ${startDate}${endDate ? ", ending " + endDate : ""}`;
  }

  setDefaultSubscriptionDates() {
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    const todayStr = this.formatDateForDisplay(today);
    const thirtyDaysStr = this.formatDateForDisplay(thirtyDaysFromNow);

    // Set default subscription name based on report/dashboard title if available
    if (this.subscribeItem?.title) {
      const defaultSubscriptionName = `${this.subscribeItem.title} Subscription`;
      this.scheduleForm
        .get("subscriptionName")
        ?.setValue(defaultSubscriptionName);
    } else if (this.isPageModeEdit && this.selectedSubscription?.title) {
      const defaultSubscriptionName = `${this.selectedSubscription.title} Subscription`;
      this.scheduleForm
        .get("subscriptionName")
        ?.setValue(defaultSubscriptionName);
    }

    this.scheduleForm.get("startDate")?.setValue(todayStr);
    this.scheduleForm.get("endDate")?.setValue(thirtyDaysStr);
  }

  formatDateForDisplay(dateValue: any): string {
    if (!dateValue) {
      return "";
    }

    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      return "";
    }

    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  }

  formatDateForAPI(dateValue: any): string {
    if (!dateValue) {
      return "";
    }

    // If the value is a string in MM/DD/YYYY format
    if (typeof dateValue === "string") {
      const parsedDate = this.parseDate(dateValue);
      if (parsedDate) {
        const year = parsedDate.getFullYear();
        const month = (parsedDate.getMonth() + 1).toString().padStart(2, "0");
        const day = parsedDate.getDate().toString().padStart(2, "0");
        return `${year}-${month}-${day}`;
      }
      return "";
    }

    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      return "";
    }

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  validateSubscriptionDates(): boolean {
    this.validationErrors = {};
    this.invalidDateParameters = [];

    const subscriptionName = this.scheduleForm.get("subscriptionName")?.value;
    const startDate = this.scheduleForm.get("startDate")?.value;
    const endDate = this.scheduleForm.get("endDate")?.value;
    const frequency = this.scheduleForm.get("frequency")?.value;
    const dailyOption = this.scheduleForm.get("dailyOption")?.value;

    if (!subscriptionName) {
      this.validationErrors["subscriptionName"] =
        "Subscription name is required";
      return false;
    }

    if (!startDate) {
      this.validationErrors["startDate"] = "Start date is required";
      return false;
    }

    // Validate required date parameters (case-insensitive)
    const requiredDateParams = ['pdate', 'pasofdate', 'pbegindate', 'pstartdate', 'penddate', 'pdaterange', 'ppersondate'];
    const formControls = Object.keys(this.scheduleForm.controls);
    
    for (const paramName of formControls) {
      const normalizedParamName = paramName.toLowerCase();
      if (requiredDateParams.includes(normalizedParamName)) {
        const paramValue = this.scheduleForm.get(paramName)?.value;
        // Check if value is empty (null, undefined, empty string, or empty array)
        const isEmpty = !paramValue || 
                       (typeof paramValue === 'string' && paramValue.trim() === '') ||
                       (Array.isArray(paramValue) && paramValue.length === 0);
        
        if (isEmpty) {
          this.validationErrors[paramName] = "Please select a value";
          this.invalidDateParameters.push(paramName);
        }
      }
    }

    if (this.invalidDateParameters.length > 0) {
      this.showValidationAlert = true;
      return false;
    }

    // Validate daily schedule selection
    if (frequency === "daily" && dailyOption === "onDays") {
      const selectedDays = this.daysOfWeek.some(day => 
        this.scheduleForm.get(day.initialValue)?.value
      );
      
      if (!selectedDays) {
        this.validationErrors["dailySchedule"] = "Please select at least one day";
        this.showValidationAlert = true;
        return false;
      }
    }

    if (endDate) {
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);

      if (endDateObj <= startDateObj) {
        this.validationErrors["endDate"] = "End date must be after start date";
        return false;
      }
    }

    return true;
  }

  validateDateFormat(field: string) {
    this.validationErrors[field] = "";
    const value = this.scheduleForm.get(field)?.value;

    if (!value) {
      if (field === "endDate") {
        return; // End date can be empty
      }

      if (field === "startDate") {
        // If startDate is empty, set it to today's date and format it properly
        const today = new Date();
        const formattedDate = this.formatDateForDisplay(today);
        this.scheduleForm.get(field)?.setValue(formattedDate);
        return;
      }
      return;
    }

    // If the value is already a Date object
    if (value instanceof Date && !isNaN(value.getTime())) {
      if (field === "endDate") {
        const startDate = this.scheduleForm.get("startDate")?.value;
        if (startDate && value <= new Date(startDate)) {
          this.validationErrors[field] = "End date must be after start date";
        }
      }
      // Format the date for display in the input field
      const month = (value.getMonth() + 1).toString().padStart(2, "0");
      const day = value.getDate().toString().padStart(2, "0");
      const year = value.getFullYear();
      this.scheduleForm.get(field)?.setValue(`${month}/${day}/${year}`);
      return;
    }

    // Handle string input
    if (typeof value === "string") {
      // Check if EXACTLY in MM/DD/YYYY format - strict validation with start/end anchors
      const datePattern =
        /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(\d{4})$/;
      if (datePattern.test(value)) {
        const parts = value.split("/");
        const month = parseInt(parts[0], 10) - 1;
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        // Additional validation for valid date (e.g., no Feb 30)
        const date = new Date(year, month, day);
        if (
          !isNaN(date.getTime()) &&
          date.getFullYear() === year &&
          date.getMonth() === month &&
          date.getDate() === day
        ) {
          // Valid date - keep the string format in the input
          if (field === "endDate") {
            const startDateVal = this.scheduleForm.get("startDate")?.value;
            const startDate =
              typeof startDateVal === "string"
                ? this.parseDate(startDateVal)
                : startDateVal;

            if (startDate && date <= new Date(startDate)) {
              this.validationErrors[field] =
                "End date must be after start date";
            }
          }
          return;
        }
      }

      // For any other format or invalid date, try to parse it
      const parsedDate = this.parseDate(value);
      if (parsedDate && !isNaN(parsedDate.getTime())) {
        // Even if we successfully parse the date, if it had extra characters, mark it invalid
        if (!/^[0-9\/]+$/.test(value) || value.length > 10) {
          this.validationErrors[field] =
            "Please enter a valid date in MM/DD/YYYY format";
          return;
        }

        const month = (parsedDate.getMonth() + 1).toString().padStart(2, "0");
        const day = parsedDate.getDate().toString().padStart(2, "0");
        const year = parsedDate.getFullYear();

        // Format correctly and update input
        this.scheduleForm.get(field)?.setValue(`${month}/${day}/${year}`);

        if (field === "endDate") {
          const startDateVal = this.scheduleForm.get("startDate")?.value;
          const startDate =
            typeof startDateVal === "string"
              ? this.parseDate(startDateVal)
              : startDateVal;

          if (startDate && parsedDate <= new Date(startDate)) {
            this.validationErrors[field] = "End date must be after start date";
          }
        }
        return;
      }

      this.validationErrors[field] =
        "Please enter a valid date in MM/DD/YYYY format";
    }
  }

  ensureStartDateHasValue() {
    const startDateValue = this.scheduleForm.get("startDate")?.value;
    if (!startDateValue) {
      const today = new Date();
      const formattedDate = this.formatDateForDisplay(today);
      this.scheduleForm.get("startDate")?.setValue(formattedDate);
    }
  }

  formatDateInput(field: string, event: any) {
    // Get the current input value
    let input = event.target.value;

    // Remove any non-numeric characters
    input = input.replace(/\D/g, "");

    // Format with slashes
    if (input.length > 0) {
      // Add first slash after MM
      if (input.length > 2) {
        input = input.substring(0, 2) + "/" + input.substring(2);
      }

      // Add second slash after DD
      if (input.length > 5) {
        input = input.substring(0, 5) + "/" + input.substring(5);
      }

      // Limit to MM/DD/YYYY format (10 characters)
      if (input.length > 10) {
        input = input.substring(0, 10);
      }
    }

    // Update the form control value
    this.scheduleForm.get(field)?.setValue(input);
  }

  parseDate(dateStr: string): Date | null {
    // Clean the input - only allow digits and slashes
    const cleanInput = dateStr.replace(/[^0-9\/]/g, "");
    if (cleanInput !== dateStr) {
      return null; // Contained invalid characters
    }

    // Try MM/DD/YYYY format - must be exact match with no extra characters
    const strictPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    let match = strictPattern.exec(dateStr);

    if (match) {
      const month = parseInt(match[1], 10) - 1;
      const day = parseInt(match[2], 10);
      const year = parseInt(match[3], 10);

      // Create date and verify consistency to catch invalid dates like 02/31/2023
      const date = new Date(year, month, day);
      if (
        !isNaN(date.getTime()) &&
        date.getFullYear() === year &&
        date.getMonth() === month &&
        date.getDate() === day
      ) {
        return date;
      }
      return null;
    }

    // Try M/D/YYYY format with looser validation
    match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(dateStr);
    if (match) {
      const month = parseInt(match[1], 10) - 1;
      const day = parseInt(match[2], 10);
      const year = parseInt(match[3], 10);

      // Additional validation for realistic dates
      if (month < 0 || month > 11 || day < 1 || day > 31) {
        return null;
      }

      // Create date and verify consistency to catch invalid dates
      const date = new Date(year, month, day);
      if (
        !isNaN(date.getTime()) &&
        date.getFullYear() === year &&
        date.getMonth() === month &&
        date.getDate() === day
      ) {
        return date;
      }
      return null;
    }

    // Try YYYY-MM-DD format
    match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(dateStr);
    if (match) {
      const year = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1;
      const day = parseInt(match[3], 10);

      if (month < 0 || month > 11 || day < 1 || day > 31) {
        return null;
      }

      const date = new Date(year, month, day);
      if (
        !isNaN(date.getTime()) &&
        date.getFullYear() === year &&
        date.getMonth() === month &&
        date.getDate() === day
      ) {
        return date;
      }
      return null;
    }

    return null; // If we get here, couldn't parse the date
  }

  // Method to sync time when form values change
  onHourChanged() {
    // No need to automatically update AM/PM based on hour now,
    // since we're using a 12-hour format (1-12)
  }

  // Method to handle AM/PM changes
  onAmPmChanged() {
    // No need to convert hours when AM/PM changes,
    // since we're now using a 12-hour format
  }

  // Helper method to convert form time values (Central display) to Eastern 24-hour format for cron
  private getHourIn24Format(): number {
    const centralHour12 = parseInt(this.scheduleForm.get("time").value);
    const centralAmpm = this.scheduleForm.get("ampm").value;
    const centralMinute = parseInt(this.scheduleForm.get("minute").value) || 0;

    // Convert Central display time to Eastern storage time
    const easternTime = this.convertCentralToEasternStorage(centralHour12, centralMinute, centralAmpm);
    
    return easternTime.hour24;
  }

  /**
   * Check user notification settings and update form state accordingly
   */
  private checkUserNotificationSettings(): void {
    this.isLoadingSettings = true;
    
    this.settingsService.getUserSettings().subscribe({
      next: (response: UserSettingsResponse) => {
        this.isLoadingSettings = false;
        
        // Handle standardized API response format with settings and featureFlags properties
        if (response && response.settings && Array.isArray(response.settings)) {
          const settingsArray = response.settings;
          
          if (settingsArray.length > 0) {
            const userSettings = settingsArray[0];
            this.globalNotificationsEnabled = userSettings.notificationInd === 'Y';
          } else {
            // If no settings found, assume notifications are enabled
            this.globalNotificationsEnabled = false;
          }
        } else {
          // Fallback: if response doesn't match expected format, assume notifications enabled
          this.globalNotificationsEnabled = false;
        }
        
        // Force update notification state even if form might not be "ready"
        this.forceUpdateNotificationState();
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.isLoadingSettings = false;
        console.error('Error fetching user settings:', error);
        // On error, assume notifications are enabled to avoid blocking the user
        this.globalNotificationsEnabled = false;
        this.forceUpdateNotificationState();
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Update notification form state based on global settings
   */
  private updateNotificationFormState(): void {
    if (!this.scheduleForm || this.notificationStateSet) {
      return;
    }

    const notificationControl = this.scheduleForm.get('notification');
    
    if (!this.globalNotificationsEnabled) {
      // Show alert and disable notification toggle in OFF position
      this.showNotificationAlert = true;
      notificationControl?.setValue(false);
      notificationControl?.disable();
    } else {
      // Hide alert and enable notification toggle with default ON value
      this.showNotificationAlert = false;
      notificationControl?.enable();
      // Only set to true if not already set (preserve existing values in edit mode)
      if (notificationControl?.value === null || notificationControl?.value === undefined) {
        notificationControl?.setValue(true);
      }
    }
    
    this.notificationStateSet = true;
  }

  /**
   * Force update notification state regardless of flags - used when settings are loaded
   */
  private forceUpdateNotificationState(): void {
    if (!this.scheduleForm) {
      return;
    }

    // Reset the flag to allow update
    this.notificationStateSet = false;
    
    const notificationControl = this.scheduleForm.get('notification');
    
    if (!this.globalNotificationsEnabled) {
      // Show alert and disable notification toggle in OFF position
      this.showNotificationAlert = true;
      notificationControl?.setValue(false);
      notificationControl?.disable();
    } else {
      // Hide alert and enable notification toggle with default ON value
      this.showNotificationAlert = false;
      notificationControl?.enable();
      // Set to true by default when global notifications are enabled
      // Only preserve existing true values in edit mode, but default to true if false/null/undefined
      if (this.isPageModeEdit && notificationControl?.value === true) {
        // In edit mode, preserve existing true value
      } else {
        // Default to true for new subscriptions or when transitioning from disabled state
        notificationControl?.setValue(true);
      }
    }
    
    this.notificationStateSet = true;
  }

  /**
   * Set up page visibility listener to refresh settings when user returns
   */
  private setupPageVisibilityListener(): void {
    this.visibilityChangeHandler = () => {
      if (!document.hidden) {
        // Page became visible - user returned to tab
        // Clear cache and refetch settings
        this.settingsService.clearCache();
        this.checkUserNotificationSettings();
      }
    };
    
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  /**
   * Open settings page in a new tab
   */
  openSettingsPage(): void {
    window.open('/settings', '_blank');
  }

  /**
   * Convert Eastern time to Central time for display
   * @param easternHour24 Hour in 24-hour Eastern time format (0-23)
   * @param easternMinute Minute in Eastern time
   * @returns Object with Central time in 12-hour format
   */
  private convertEasternToCentralDisplay(easternHour24: number, easternMinute: number = 0): { hour: number, minute: number, ampm: string } {
    // Convert Eastern to Central (subtract 1 hour)
    let centralHour24 = easternHour24 - 1;
    let centralMinute = easternMinute;
    
    // Handle day boundary crossing
    if (centralHour24 < 0) {
      centralHour24 = 23;
      // Note: We're not handling date changes here since we only care about time
    }
    
    // Convert 24-hour to 12-hour format
    let centralHour12: number;
    let ampm: string;
    
    if (centralHour24 === 0) {
      centralHour12 = 12;
      ampm = 'AM';
    } else if (centralHour24 < 12) {
      centralHour12 = centralHour24;
      ampm = 'AM';
    } else if (centralHour24 === 12) {
      centralHour12 = 12;
      ampm = 'PM';
    } else {
      centralHour12 = centralHour24 - 12;
      ampm = 'PM';
    }
    
    return {
      hour: centralHour12,
      minute: centralMinute,
      ampm: ampm
    };
  }

  /**
   * Convert Central time to Eastern time for storage
   * @param centralHour12 Hour in 12-hour Central time format (1-12)
   * @param centralMinute Minute in Central time
   * @param centralAmpm AM/PM indicator
   * @returns Object with Eastern time in 24-hour format
   */
  private convertCentralToEasternStorage(centralHour12: number, centralMinute: number, centralAmpm: string): { hour24: number, minute: number } {
    // Convert Central 12-hour to 24-hour format
    let centralHour24: number;
    
    if (centralAmpm === 'AM') {
      centralHour24 = centralHour12 === 12 ? 0 : centralHour12;
    } else { // PM
      centralHour24 = centralHour12 === 12 ? 12 : centralHour12 + 12;
    }
    
    // Convert Central to Eastern (add 1 hour)
    let easternHour24 = centralHour24 + 1;
    let easternMinute = centralMinute;
    
    // Handle day boundary crossing
    if (easternHour24 >= 24) {
      easternHour24 = 0;
      // Note: We're not handling date changes here since we only care about time
    }
    
    return {
      hour24: easternHour24,
      minute: easternMinute
    };
  }

  /**
   * Cleanup method to remove event listeners
   */
  ngOnDestroy(): void {
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
  }
}
